import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Link } from '../components'
import { ORDERS_MODES } from './config'
import {
  HeaderRow,
  NewBadge,
  OptionLabel,
  SegmentButton,
  SegmentGroup,
} from './OrdersModeSwitcher.styles'
import { useIsBulkOrderEnabled } from './useIsBulkOrderEnabled'

/**
 * Pair / Bulk orders mode switcher in the My Orders box header.
 * Navigates between pair history (`/`) and bulk history (`/bulk-orders`).
 */
const OrdersModeSwitcher = ({ title }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isBulkOrderEnabledForUser = useIsBulkOrderEnabled()

  const activeMode = location.pathname.startsWith(Link.BULK_ORDERS)
    ? ORDERS_MODES.BULK
    : ORDERS_MODES.PAIR

  const options = useMemo(
    () => [
      {
        value: ORDERS_MODES.PAIR,
        label: t('Pair orders'),
        showNew: false,
        to: Link.HOME,
      },
      {
        value: ORDERS_MODES.BULK,
        label: t('Bulk orders'),
        showNew: true,
        to: Link.BULK_ORDERS,
      },
    ],
    [t],
  )

  if (!isBulkOrderEnabledForUser) {
    return title
  }

  const newLabel = t('new')

  return (
    <HeaderRow>
      {title}
      <SegmentGroup role="tablist" aria-label="Orders mode">
        {options.map(option => (
          <SegmentButton
            key={option.value}
            type="button"
            role="tab"
            aria-selected={activeMode === option.value}
            toggle
            active={activeMode === option.value}
            onClick={() => {
              if (option.to !== location.pathname) {
                navigate(option.to)
              }
            }}
          >
            <OptionLabel>
              {option.label}
              {option.showNew && <NewBadge>{newLabel}</NewBadge>}
            </OptionLabel>
          </SegmentButton>
        ))}
      </SegmentGroup>
    </HeaderRow>
  )
}

OrdersModeSwitcher.propTypes = {
  title: PropTypes.node.isRequired,
}

export default OrdersModeSwitcher
